import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FiArrowLeft, FiDownload, FiBookmark, FiShare2, FiExternalLink,
  FiList, FiMaximize2, FiMinimize2,
  FiChevronUp, FiCopy, FiCheck, FiFileText, FiImage, FiCode,
  FiUpload, FiX, FiZoomIn, FiZoomOut, FiDroplet
} from 'react-icons/fi';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import rust from 'react-syntax-highlighter/dist/esm/languages/prism/rust';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import java from 'react-syntax-highlighter/dist/esm/languages/prism/java';
import cpp from 'react-syntax-highlighter/dist/esm/languages/prism/cpp';
import sql from 'react-syntax-highlighter/dist/esm/languages/prism/sql';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import jsx from 'react-syntax-highlighter/dist/esm/languages/prism/jsx';
import docker from 'react-syntax-highlighter/dist/esm/languages/prism/docker';
import { apiGetResearchById } from '../../api/researchApi';
import { generateAllCitations, CITATION_FORMATS } from '../../utils/citationGenerator';
import UserBadge from '../../components/UserBadge';
import InteractiveChart from '../../components/InteractiveChart';

SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('js', javascript);
SyntaxHighlighter.registerLanguage('typescript', typescript);
SyntaxHighlighter.registerLanguage('ts', typescript);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('shell', bash);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('yaml', yaml);
SyntaxHighlighter.registerLanguage('yml', yaml);
SyntaxHighlighter.registerLanguage('rust', rust);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('cpp', cpp);
SyntaxHighlighter.registerLanguage('c++', cpp);
SyntaxHighlighter.registerLanguage('sql', sql);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('jsx', jsx);
SyntaxHighlighter.registerLanguage('docker', docker);
SyntaxHighlighter.registerLanguage('dockerfile', docker);

/* ─── Reader theme swatches ─────────────────────────────────── */
const READER_THEMES = [
  {
    id: 'default', name: 'Default', dark: false,
    preview: ['#ffffff', '#0052cc'],
    chartColors: ['#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#f97316', '#14b8a6'],
    vars: {
      '--pr-bg': '#ffffff', '--pr-text': '#1a1a2e', '--pr-accent': '#0052cc',
      '--pr-accent-soft': '#f0f4ff', '--pr-surface': '#ffffff', '--pr-border': '#e5e7eb',
      '--pr-muted': '#888888', '--pr-hover': '#f0f0f5', '--pr-tag-bg': '#f0f0f5',
      '--pr-tag-text': '#555555', '--pr-code-bg': '#1e1e2e', '--pr-code-header': '#2a2a3e',
      '--pr-code-header-text': '#888', '--pr-th-bg': '#f5f5fa', '--pr-th-text': '#333',
      '--pr-td-border': '#e5e7eb', '--pr-section-border': '#f0f0f5',
      '--pr-figure-bg': '#f5f5fa', '--pr-figure-border': '#e5e7eb',
      '--pr-progress-track': '#e5e7eb', '--pr-highlight-row': '#f0f8ff',
      '--ic-bg': '#ffffff', '--ic-border': '#e5e7eb', '--ic-title': '#1e293b',
      '--ic-caption': '#64748b', '--ic-axis': '#94a3b8', '--ic-tick': '#475569',
      '--ic-grid': '#f1f5f9', '--ic-axis-line': '#e2e8f0',
      '--ic-tooltip-bg': '#fff', '--ic-tooltip-border': '#e2e8f0', '--ic-tooltip-text': '#1e293b',
      '--ic-fig-bg': 'rgba(0,82,204,0.08)', '--ic-fig-text': '#0052cc',
      '--ic-dl-border': '#e2e8f0', '--ic-dl-color': '#94a3b8', '--ic-dl-hover': '#0052cc',
    },
  },
  {
    id: 'sepia', name: 'Sepia', dark: false,
    preview: ['#f8f1e3', '#8b6914'],
    chartColors: ['#8b6914', '#b45309', '#a16207', '#ca8a04', '#d97706', '#92400e', '#78350f', '#854d0e', '#c2410c', '#ea580c'],
    vars: {
      '--pr-bg': '#f8f1e3', '--pr-text': '#433422', '--pr-accent': '#8b6914',
      '--pr-accent-soft': '#ede4cc', '--pr-surface': '#f0e8d4', '--pr-border': '#d4c9a8',
      '--pr-muted': '#8a7b65', '--pr-hover': '#ede4cc', '--pr-tag-bg': '#ede4cc',
      '--pr-tag-text': '#6b5c42', '--pr-code-bg': '#2b2418', '--pr-code-header': '#3d3325',
      '--pr-code-header-text': '#a89878', '--pr-th-bg': '#ede4cc', '--pr-th-text': '#433422',
      '--pr-td-border': '#d4c9a8', '--pr-section-border': '#d4c9a8',
      '--pr-figure-bg': '#ede4cc', '--pr-figure-border': '#d4c9a8',
      '--pr-progress-track': '#d4c9a8', '--pr-highlight-row': '#ede4cc',
      '--ic-bg': '#f8f1e3', '--ic-border': '#d4c9a8', '--ic-title': '#433422',
      '--ic-caption': '#8a7b65', '--ic-axis': '#a89878', '--ic-tick': '#6b5c42',
      '--ic-grid': '#ede4cc', '--ic-axis-line': '#d4c9a8',
      '--ic-tooltip-bg': '#f0e8d4', '--ic-tooltip-border': '#d4c9a8', '--ic-tooltip-text': '#433422',
      '--ic-fig-bg': 'rgba(139,105,20,0.1)', '--ic-fig-text': '#8b6914',
      '--ic-dl-border': '#d4c9a8', '--ic-dl-color': '#8a7b65', '--ic-dl-hover': '#8b6914',
    },
  },
  {
    id: 'slate', name: 'Slate', dark: false,
    preview: ['#f8fafc', '#6366f1'],
    chartColors: ['#6366f1', '#8b5cf6', '#a78bfa', '#818cf8', '#c084fc', '#7c3aed', '#4f46e5', '#6d28d9', '#9333ea', '#a855f7'],
    vars: {
      '--pr-bg': '#f8fafc', '--pr-text': '#0f172a', '--pr-accent': '#6366f1',
      '--pr-accent-soft': '#eef2ff', '--pr-surface': '#f1f5f9', '--pr-border': '#e2e8f0',
      '--pr-muted': '#94a3b8', '--pr-hover': '#e2e8f0', '--pr-tag-bg': '#e2e8f0',
      '--pr-tag-text': '#475569', '--pr-code-bg': '#1e1b4b', '--pr-code-header': '#2e2a5e',
      '--pr-code-header-text': '#a5b4fc', '--pr-th-bg': '#e2e8f0', '--pr-th-text': '#1e293b',
      '--pr-td-border': '#e2e8f0', '--pr-section-border': '#e2e8f0',
      '--pr-figure-bg': '#f1f5f9', '--pr-figure-border': '#e2e8f0',
      '--pr-progress-track': '#e2e8f0', '--pr-highlight-row': '#eef2ff',
      '--ic-bg': '#f8fafc', '--ic-border': '#e2e8f0', '--ic-title': '#1e293b',
      '--ic-caption': '#64748b', '--ic-axis': '#94a3b8', '--ic-tick': '#475569',
      '--ic-grid': '#eef2ff', '--ic-axis-line': '#e2e8f0',
      '--ic-tooltip-bg': '#f8fafc', '--ic-tooltip-border': '#e2e8f0', '--ic-tooltip-text': '#0f172a',
      '--ic-fig-bg': 'rgba(99,102,241,0.08)', '--ic-fig-text': '#6366f1',
      '--ic-dl-border': '#e2e8f0', '--ic-dl-color': '#94a3b8', '--ic-dl-hover': '#6366f1',
    },
  },
  {
    id: 'mint', name: 'Mint', dark: false,
    preview: ['#f0fdf4', '#059669'],
    chartColors: ['#059669', '#10b981', '#34d399', '#047857', '#065f46', '#0d9488', '#14b8a6', '#0891b2', '#06b6d4', '#2dd4bf'],
    vars: {
      '--pr-bg': '#f0fdf4', '--pr-text': '#14532d', '--pr-accent': '#059669',
      '--pr-accent-soft': '#dcfce7', '--pr-surface': '#ecfdf5', '--pr-border': '#bbf7d0',
      '--pr-muted': '#6b8f71', '--pr-hover': '#dcfce7', '--pr-tag-bg': '#dcfce7',
      '--pr-tag-text': '#166534', '--pr-code-bg': '#0f2616', '--pr-code-header': '#1a3820',
      '--pr-code-header-text': '#6ee7b7', '--pr-th-bg': '#dcfce7', '--pr-th-text': '#14532d',
      '--pr-td-border': '#bbf7d0', '--pr-section-border': '#bbf7d0',
      '--pr-figure-bg': '#ecfdf5', '--pr-figure-border': '#bbf7d0',
      '--pr-progress-track': '#bbf7d0', '--pr-highlight-row': '#dcfce7',
      '--ic-bg': '#f0fdf4', '--ic-border': '#bbf7d0', '--ic-title': '#14532d',
      '--ic-caption': '#6b8f71', '--ic-axis': '#6b8f71', '--ic-tick': '#166534',
      '--ic-grid': '#dcfce7', '--ic-axis-line': '#bbf7d0',
      '--ic-tooltip-bg': '#ecfdf5', '--ic-tooltip-border': '#bbf7d0', '--ic-tooltip-text': '#14532d',
      '--ic-fig-bg': 'rgba(5,150,105,0.1)', '--ic-fig-text': '#059669',
      '--ic-dl-border': '#bbf7d0', '--ic-dl-color': '#6b8f71', '--ic-dl-hover': '#059669',
    },
  },
  {
    id: 'rose', name: 'Rosé', dark: false,
    preview: ['#fff5f7', '#e11d48'],
    chartColors: ['#e11d48', '#f43f5e', '#fb7185', '#be123c', '#9f1239', '#ec4899', '#db2777', '#c026d3', '#a21caf', '#f472b6'],
    vars: {
      '--pr-bg': '#fff5f7', '--pr-text': '#4a1931', '--pr-accent': '#e11d48',
      '--pr-accent-soft': '#ffe4e6', '--pr-surface': '#fff1f2', '--pr-border': '#fecdd3',
      '--pr-muted': '#a3707d', '--pr-hover': '#ffe4e6', '--pr-tag-bg': '#ffe4e6',
      '--pr-tag-text': '#9f1239', '--pr-code-bg': '#2a0e1a', '--pr-code-header': '#3d1528',
      '--pr-code-header-text': '#fda4af', '--pr-th-bg': '#ffe4e6', '--pr-th-text': '#4a1931',
      '--pr-td-border': '#fecdd3', '--pr-section-border': '#fecdd3',
      '--pr-figure-bg': '#fff1f2', '--pr-figure-border': '#fecdd3',
      '--pr-progress-track': '#fecdd3', '--pr-highlight-row': '#ffe4e6',
      '--ic-bg': '#fff5f7', '--ic-border': '#fecdd3', '--ic-title': '#4a1931',
      '--ic-caption': '#a3707d', '--ic-axis': '#a3707d', '--ic-tick': '#9f1239',
      '--ic-grid': '#ffe4e6', '--ic-axis-line': '#fecdd3',
      '--ic-tooltip-bg': '#fff1f2', '--ic-tooltip-border': '#fecdd3', '--ic-tooltip-text': '#4a1931',
      '--ic-fig-bg': 'rgba(225,29,72,0.08)', '--ic-fig-text': '#e11d48',
      '--ic-dl-border': '#fecdd3', '--ic-dl-color': '#a3707d', '--ic-dl-hover': '#e11d48',
    },
  },
  {
    id: 'dark', name: 'Dark', dark: true,
    preview: ['#1a1a2e', '#5ca0ff'],
    chartColors: ['#5ca0ff', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#22d3ee', '#f472b6', '#c084fc', '#fb923c', '#2dd4bf'],
    vars: {
      '--pr-bg': '#1a1a2e', '--pr-text': '#d0d0e0', '--pr-accent': '#5ca0ff',
      '--pr-accent-soft': '#1e2a4a', '--pr-surface': '#12122a', '--pr-border': '#2a2a4a',
      '--pr-muted': '#8888aa', '--pr-hover': '#2a2a4a', '--pr-tag-bg': '#2a2a4a',
      '--pr-tag-text': '#8888cc', '--pr-code-bg': '#0d0d1a', '--pr-code-header': '#1a1a30',
      '--pr-code-header-text': '#8888aa', '--pr-th-bg': '#1a1a30', '--pr-th-text': '#c0c0d0',
      '--pr-td-border': '#2a2a4a', '--pr-section-border': '#2a2a4a',
      '--pr-figure-bg': '#12122a', '--pr-figure-border': '#2a2a4a',
      '--pr-progress-track': '#2a2a4a', '--pr-highlight-row': '#1e2a4a',
      '--ic-bg': '#1a1a2e', '--ic-border': '#2a2a4a', '--ic-title': '#d0d0e0',
      '--ic-caption': '#8888aa', '--ic-axis': '#555580', '--ic-tick': '#8888aa',
      '--ic-grid': '#22223a', '--ic-axis-line': '#2a2a4a',
      '--ic-tooltip-bg': '#12122a', '--ic-tooltip-border': '#2a2a4a', '--ic-tooltip-text': '#d0d0e0',
      '--ic-fig-bg': 'rgba(92,160,255,0.12)', '--ic-fig-text': '#5ca0ff',
      '--ic-dl-border': '#2a2a4a', '--ic-dl-color': '#555580', '--ic-dl-hover': '#5ca0ff',
    },
  },
  {
    id: 'midnight', name: 'Midnight', dark: true,
    preview: ['#0f0f1a', '#a78bfa'],
    chartColors: ['#a78bfa', '#c084fc', '#818cf8', '#e879f9', '#f472b6', '#67e8f9', '#34d399', '#fbbf24', '#fb923c', '#f87171'],
    vars: {
      '--pr-bg': '#0f0f1a', '--pr-text': '#e2e0f0', '--pr-accent': '#a78bfa',
      '--pr-accent-soft': '#1f1b3d', '--pr-surface': '#161628', '--pr-border': '#2d2b50',
      '--pr-muted': '#7c7aa0', '--pr-hover': '#1f1b3d', '--pr-tag-bg': '#1f1b3d',
      '--pr-tag-text': '#c4b5fd', '--pr-code-bg': '#0a0a14', '--pr-code-header': '#1a1830',
      '--pr-code-header-text': '#8b85b8', '--pr-th-bg': '#1a1830', '--pr-th-text': '#d4d0f0',
      '--pr-td-border': '#2d2b50', '--pr-section-border': '#2d2b50',
      '--pr-figure-bg': '#161628', '--pr-figure-border': '#2d2b50',
      '--pr-progress-track': '#2d2b50', '--pr-highlight-row': '#1f1b3d',
      '--ic-bg': '#0f0f1a', '--ic-border': '#2d2b50', '--ic-title': '#e2e0f0',
      '--ic-caption': '#7c7aa0', '--ic-axis': '#4a4870', '--ic-tick': '#7c7aa0',
      '--ic-grid': '#1a1830', '--ic-axis-line': '#2d2b50',
      '--ic-tooltip-bg': '#161628', '--ic-tooltip-border': '#2d2b50', '--ic-tooltip-text': '#e2e0f0',
      '--ic-fig-bg': 'rgba(167,139,250,0.12)', '--ic-fig-text': '#a78bfa',
      '--ic-dl-border': '#2d2b50', '--ic-dl-color': '#4a4870', '--ic-dl-hover': '#a78bfa',
    },
  },
  {
    id: 'nord', name: 'Nord', dark: true,
    preview: ['#2e3440', '#88c0d0'],
    chartColors: ['#88c0d0', '#81a1c1', '#5e81ac', '#a3be8c', '#ebcb8b', '#bf616a', '#d08770', '#b48ead', '#8fbcbb', '#a3be8c'],
    vars: {
      '--pr-bg': '#2e3440', '--pr-text': '#eceff4', '--pr-accent': '#88c0d0',
      '--pr-accent-soft': '#3b4252', '--pr-surface': '#3b4252', '--pr-border': '#434c5e',
      '--pr-muted': '#81a1c1', '--pr-hover': '#434c5e', '--pr-tag-bg': '#434c5e',
      '--pr-tag-text': '#88c0d0', '--pr-code-bg': '#242933', '--pr-code-header': '#2e3440',
      '--pr-code-header-text': '#d8dee9', '--pr-th-bg': '#3b4252', '--pr-th-text': '#eceff4',
      '--pr-td-border': '#434c5e', '--pr-section-border': '#434c5e',
      '--pr-figure-bg': '#3b4252', '--pr-figure-border': '#434c5e',
      '--pr-progress-track': '#434c5e', '--pr-highlight-row': '#3b4252',
      '--ic-bg': '#2e3440', '--ic-border': '#434c5e', '--ic-title': '#eceff4',
      '--ic-caption': '#81a1c1', '--ic-axis': '#4c566a', '--ic-tick': '#81a1c1',
      '--ic-grid': '#3b4252', '--ic-axis-line': '#434c5e',
      '--ic-tooltip-bg': '#3b4252', '--ic-tooltip-border': '#434c5e', '--ic-tooltip-text': '#eceff4',
      '--ic-fig-bg': 'rgba(136,192,208,0.12)', '--ic-fig-text': '#88c0d0',
      '--ic-dl-border': '#434c5e', '--ic-dl-color': '#4c566a', '--ic-dl-hover': '#88c0d0',
    },
  },
];

/* ─── Sample rich paper content generator ──────────────────── */
function generatePaperContent(paper) {
  if (paper.sections) return paper.sections;

  return [
    {
      id: 'abstract',
      title: 'Abstract',
      type: 'text',
      content: paper.abstract,
    },
    {
      id: 'introduction',
      title: '1. Introduction',
      type: 'text',
      content: `The rapid advancement of large language models (LLMs) has created unprecedented opportunities for automation across enterprise workflows. In this paper, we explore the practical implications of deploying ${paper.category.toLowerCase()} systems in production environments.\n\nTraditional approaches to ${paper.tags[0] || 'automation'} have relied on rigid, rule-based systems that struggle to adapt to novel inputs. Our work demonstrates that modern AI architectures can achieve significantly better results while reducing maintenance burden.\n\nThe key contributions of this work include:\n• A novel framework for ${paper.title.toLowerCase().includes('agent') ? 'agent orchestration' : 'system design'} that reduces deployment time by 40–70%\n• Comprehensive benchmarks across ${Math.floor(Math.random() * 10 + 5)} enterprise use cases\n• Open-source reference implementations available for immediate deployment\n• Practical guidelines for teams transitioning from legacy automation to AI-native workflows`,
    },
    {
      id: 'background',
      title: '2. Background & Related Work',
      type: 'text',
      content: `Prior work in ${paper.category} has established several foundational approaches. Notable contributions include transformer-based architectures for sequence processing (Vaswani et al., 2017), retrieval-augmented generation for knowledge-intensive tasks (Lewis et al., 2020), and chain-of-thought prompting for complex reasoning (Wei et al., 2022).\n\nHowever, most existing solutions focus on isolated tasks rather than end-to-end production systems. Our work bridges this gap by providing a holistic framework that addresses deployment, monitoring, error handling, and continuous improvement in real-world settings.\n\nRecent industry reports indicate that ${Math.floor(Math.random() * 30 + 60)}% of enterprises are actively exploring AI automation, yet fewer than 20% have successfully deployed production systems. We attribute this gap to the lack of practical, battle-tested architectures — which this paper aims to address.`,
    },
    {
      id: 'methodology',
      title: '3. Methodology',
      type: 'text',
      content: `Our approach combines three key components:\n\n3.1 System Architecture\nWe employ a modular, event-driven architecture that separates concerns between task planning, execution, and evaluation. Each module can be independently scaled and updated.\n\n3.2 Evaluation Framework\nWe evaluate across multiple dimensions: accuracy, latency, reliability, cost, and maintainability. Our benchmark suite includes ${Math.floor(Math.random() * 10 + 10)} distinct tasks drawn from real production workloads.\n\n3.3 Deployment Strategy\nWe use progressive rollout with automated canary analysis, enabling safe deployment of model updates with automatic rollback on quality regression.`,
    },
    {
      id: 'code-example',
      title: '3.1 Implementation Example',
      type: 'code',
      language: 'python',
      content: `from vectorflow import Pipeline, Agent, Tool\nfrom vectorflow.llm import Claude, GPT4\n\n# Define the processing pipeline\npipeline = Pipeline(\n    name="${paper.tags[0] || 'automation'}_pipeline",\n    description="Production ${paper.category} workflow",\n)\n\n# Configure the primary agent\nagent = Agent(\n    model=Claude(model="claude-sonnet-4-20250514"),\n    tools=[\n        Tool.document_reader(),\n        Tool.web_search(),\n        Tool.code_executor(),\n    ],\n    max_steps=10,\n    retry_policy="exponential_backoff",\n)\n\n# Execute with monitoring\nresult = pipeline.run(\n    agent=agent,\n    input_data=documents,\n    callbacks=[MetricsCallback(), AlertCallback()],\n)\n\nprint(f"Processed {len(result.outputs)} items")\nprint(f"Accuracy: {result.metrics.accuracy:.2%}")\nprint(f"Latency P95: {result.metrics.p95_latency_ms}ms")`,
    },
    {
      id: 'results',
      title: '4. Results',
      type: 'text',
      content: `Our experiments demonstrate significant improvements over baseline approaches across all evaluated dimensions.\n\nKey findings:\n\n• Task Accuracy: Our system achieves ${(Math.random() * 5 + 93).toFixed(1)}% accuracy on the benchmark suite, representing a ${(Math.random() * 10 + 8).toFixed(1)}% improvement over the best existing baseline.\n\n• Latency: End-to-end processing latency averages ${Math.floor(Math.random() * 200 + 100)}ms per task, with P99 latency under ${Math.floor(Math.random() * 500 + 500)}ms — well within production requirements.\n\n• Reliability: Over a 30-day production evaluation period, the system maintained ${(Math.random() * 0.5 + 99.5).toFixed(2)}% uptime with zero data loss events.\n\n• Cost Efficiency: Total operational cost was reduced by ${Math.floor(Math.random() * 30 + 50)}% compared to the previous system, primarily through intelligent caching and model routing.`,
    },
    {
      id: 'results-table',
      title: '4.1 Benchmark Comparison',
      type: 'table',
      headers: ['Method', 'Accuracy', 'Latency (P95)', 'Cost / 1K ops', 'Reliability'],
      rows: [
        ['Rule-based Baseline', '71.2%', '45ms', '$0.00', '99.1%'],
        ['GPT-3.5 + RAG', '84.7%', '890ms', '$2.40', '98.3%'],
        ['Fine-tuned Llama 2', '88.1%', '120ms', '$0.15', '99.5%'],
        ['Claude + Tooling', '91.3%', '340ms', '$1.80', '99.7%'],
        ['Ours (VectorFlow)', '96.3%', '185ms', '$0.42', '99.9%'],
      ],
    },
    {
      id: 'figure',
      title: '4.2 Performance Over Time',
      type: 'figure',
      caption: 'Figure 1: System accuracy and latency trends over a 30-day production evaluation period. The system demonstrates consistent performance with gradual improvement as the feedback loop refines model outputs.',
      chartConfig: {
        chartType: 'line',
        title: 'Accuracy & Latency Over 30 Days',
        xKey: 'name',
        yLabel: 'Value',
        xLabel: 'Day',
        showGrid: true,
        showLegend: true,
        smooth: true,
        series: [
          { dataKey: 'accuracy', label: 'Accuracy (%)', },
          { dataKey: 'latency', label: 'Latency (ms)', },
        ],
        data: [
          { name: 'Day 1', accuracy: 91.2, latency: 310 },
          { name: 'Day 5', accuracy: 92.8, latency: 280 },
          { name: 'Day 10', accuracy: 94.1, latency: 245 },
          { name: 'Day 15', accuracy: 95.0, latency: 220 },
          { name: 'Day 20', accuracy: 95.6, latency: 198 },
          { name: 'Day 25', accuracy: 96.1, latency: 190 },
          { name: 'Day 30', accuracy: 96.3, latency: 185 },
        ],
      },
    },
    {
      id: 'chart-comparison',
      title: '4.3 Method Comparison',
      type: 'chart',
      caption: 'Figure 2: Comparison of accuracy across evaluated methods. Our approach (VectorFlow) outperforms all baselines across the benchmark suite.',
      chartConfig: {
        chartType: 'bar',
        title: 'Accuracy by Method',
        xKey: 'name',
        yLabel: 'Accuracy (%)',
        showGrid: true,
        showLegend: false,
        data: [
          { name: 'Rule-based', value: 71.2 },
          { name: 'GPT-3.5+RAG', value: 84.7 },
          { name: 'Llama 2 FT', value: 88.1 },
          { name: 'Claude+Tools', value: 91.3 },
          { name: 'VectorFlow', value: 96.3 },
        ],
      },
    },
    {
      id: 'chart-cost',
      title: '4.4 Cost Distribution',
      type: 'chart',
      caption: 'Figure 3: Operational cost breakdown by component, showing that intelligent model routing accounts for the largest cost savings.',
      chartConfig: {
        chartType: 'pie',
        title: 'Cost Distribution by Component',
        data: [
          { name: 'Model Inference', value: 42 },
          { name: 'Embedding & RAG', value: 18 },
          { name: 'Infrastructure', value: 15 },
          { name: 'Monitoring', value: 12 },
          { name: 'Storage', value: 8 },
          { name: 'Other', value: 5 },
        ],
      },
    },
    {
      id: 'discussion',
      title: '5. Discussion',
      type: 'text',
      content: `The results validate our core hypothesis: production AI systems benefit significantly from modular, observable architectures that treat reliability as a first-class concern.\n\nSeveral insights emerged from our deployment experience:\n\n5.1 Error Handling Is Critical\nThe most impactful investment was in robust error handling and graceful degradation. Production LLM systems encounter novel failure modes that rule-based fallbacks handle poorly. Our circuit-breaker pattern with intelligent retries reduced unrecoverable errors by 94%.\n\n5.2 Monitoring Drives Improvement\nComprehensive observability — tracking not just latency and errors, but semantic quality metrics — enabled rapid iteration. Teams that deployed our monitoring stack improved their system quality 3x faster than those relying on manual review.\n\n5.3 Cost Optimization Requires Architecture\nNaive LLM usage can be prohibitively expensive at scale. Our tiered routing approach — using smaller models for simple tasks and larger models only when needed — reduced costs by 60% with minimal accuracy impact.`,
    },
    {
      id: 'conclusion',
      title: '6. Conclusion',
      type: 'text',
      content: `We have presented a practical framework for deploying ${paper.category.toLowerCase()} systems that achieves state-of-the-art results while maintaining the reliability and observability required for production use.\n\nOur contributions include a modular architecture, comprehensive evaluation methodology, and battle-tested deployment strategies drawn from ${Math.floor(Math.random() * 20 + 20)}+ real-world deployments.\n\nFuture work will focus on multi-agent coordination, adaptive model selection, and federated deployment patterns for privacy-sensitive environments.\n\nAll code and evaluation benchmarks are available at github.com/vectorlabsnc/vectorflow.`,
    },
    {
      id: 'references',
      title: 'References',
      type: 'references',
      content: [
        'Vaswani, A., et al. (2017). Attention Is All You Need. NeurIPS.',
        'Lewis, P., et al. (2020). Retrieval-Augmented Generation for Knowledge-Intensive NLP Tasks. NeurIPS.',
        'Wei, J., et al. (2022). Chain-of-Thought Prompting Elicits Reasoning in Large Language Models. NeurIPS.',
        'Brown, T., et al. (2020). Language Models are Few-Shot Learners. NeurIPS.',
        'Touvron, H., et al. (2023). Llama 2: Open Foundation and Fine-Tuned Chat Models. arXiv.',
        'Anthropic. (2024). Claude 3 Technical Report. Anthropic Research.',
        'Mitchell, D., Vasquez, R. (2025). VectorFlow: An Open-Source AI Workflow Engine. Vector Labs Reports.',
        'Chase, H. (2022). LangChain: Building Applications with LLMs. GitHub.',
      ],
    },
  ];
}

/* ─── Section Renderers ────────────────────────────────────── */
function TextSection({ content }) {
  const paragraphs = content.split('\n').filter(Boolean);
  return (
    <div className="pr-text-section">
      {paragraphs.map((p, i) => {
        if (p.startsWith('•')) {
          return <li key={i} className="pr-bullet">{p.slice(1).trim()}</li>;
        }
        if (/^\d+\.\d+\s/.test(p)) {
          return <h4 key={i} className="pr-subsection-title">{p}</h4>;
        }
        return <p key={i}>{p}</p>;
      })}
    </div>
  );
}

function CodeSection({ content, language }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="pr-code-section">
      <div className="pr-code-header">
        <span><FiCode /> {language || 'code'}</span>
        <button onClick={handleCopy} className="pr-copy-btn">
          {copied ? <><FiCheck /> Copied</> : <><FiCopy /> Copy</>}
        </button>
      </div>
      <SyntaxHighlighter
        language={language || 'python'}
        style={oneDark}
        showLineNumbers
        wrapLongLines
        customStyle={{ margin: 0, borderRadius: '0 0 10px 10px', fontSize: '0.88rem' }}
      >
        {content}
      </SyntaxHighlighter>
    </div>
  );
}

function TableSection({ headers, rows }) {
  return (
    <div className="pr-table-section">
      <div className="pr-table-scroll">
        <table className="pr-data-table">
          <thead>
            <tr>{headers.map((h) => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className={i === rows.length - 1 ? 'highlight-row' : ''}>
                {row.map((cell, j) => <td key={j}>{cell}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FigureSection({ caption, imageUrl, chartConfig, themeColors }) {
  return (
    <div className="pr-figure-section">
      {chartConfig ? (
        <InteractiveChart chartConfig={chartConfig} caption={caption} themeColors={themeColors} />
      ) : imageUrl ? (
        <>
          <img src={imageUrl} alt={caption || 'Figure'} className="pr-figure-img" loading="lazy" />
          {caption && <p className="pr-figure-caption">{caption}</p>}
        </>
      ) : (
        <>
          <div className="pr-figure-placeholder">
            <FiImage size={32} />
            <span>Figure — Interactive Visualization</span>
            <div className="pr-figure-bars">
              {[85, 92, 78, 95, 88, 96, 91, 97, 93, 96].map((h, i) => (
                <div key={i} className="pr-bar" style={{ height: `${h}%`, animationDelay: `${i * 0.05}s` }} />
              ))}
            </div>
          </div>
          {caption && <p className="pr-figure-caption">{caption}</p>}
        </>
      )}
    </div>
  );
}

function ReferencesSection({ items }) {
  return (
    <ol className="pr-references-list">
      {items.map((ref, i) => (
        <li key={i} className="pr-reference">{ref}</li>
      ))}
    </ol>
  );
}

/* ─── Main Paper Reader ────────────────────────────────────── */
export default function PaperReader() {
  const { id } = useParams();
  const [paper, setPaper] = useState(null);

  useEffect(() => {
    apiGetResearchById(id).then((res) => {
      if (res.ok) setPaper(res.data);
    });
  }, [id]);

  const sections = useMemo(() => paper ? generatePaperContent(paper) : [], [paper]);

  const [tocOpen, setTocOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('');
  const [activeTheme, setActiveTheme] = useState(READER_THEMES.find(t => t.id === 'dark') || READER_THEMES[0]);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [focusMode, setFocusMode] = useState(false);
  const [readProgress, setReadProgress] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [showCiteModal, setShowCiteModal] = useState(false);
  const [citeCopied, setCiteCopied] = useState(null);
  const [savedPapers, setSavedPapers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('fl_saved_papers') || '[]'); } catch { return []; }
  });

  const isSaved = paper ? savedPapers.includes(paper.id) : false;
  const handleToggleSave = () => {
    if (!paper) return;
    const next = isSaved ? savedPapers.filter((s) => s !== paper.id) : [...savedPapers, paper.id];
    localStorage.setItem('fl_saved_papers', JSON.stringify(next));
    setSavedPapers(next);
  };

  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const fileInputRef = useRef(null);
  const themePickerRef = useRef(null);

  // Close theme picker when clicking outside
  useEffect(() => {
    if (!showThemePicker) return;
    const handleClickOutside = (e) => {
      if (themePickerRef.current && !themePickerRef.current.contains(e.target)) {
        setShowThemePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showThemePicker]);

  // Scroll spy for active section + reading progress
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setReadProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
      setShowScrollTop(scrollTop > 400);

      // Find active section
      let current = '';
      for (const section of sections) {
        const el = sectionRefs.current[section.id];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150) current = section.id;
        }
      }
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]);

  // Citations: use saved citations if available, otherwise generate on the fly
  const paperCitations = useMemo(() => {
    if (!paper) return {};
    if (paper.citations && Object.keys(paper.citations).length > 0) return paper.citations;
    return generateAllCitations(paper);
  }, [paper]);

  const handleCiteCopy = (key) => {
    navigator.clipboard.writeText(paperCitations[key] || '');
    setCiteCopied(key);
    setTimeout(() => setCiteCopied(null), 2000);
  };

  const scrollToSection = useCallback((sectionId) => {
    const el = sectionRefs.current[sectionId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handlePdfUpload = (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(URL.createObjectURL(file));
      setShowPdfViewer(true);
    }
  };

  if (!paper) {
    return (
      <div className="paper-reader">
        <div className="pr-not-found">
          <h2>Paper not found</h2>
          <Link to="/research" className="rd-back">&larr; Back to catalogue</Link>
        </div>
      </div>
    );
  }

  const readerClass = [
    'paper-reader',
    activeTheme.dark ? 'pr-dark' : '',
    focusMode ? 'pr-focus' : '',
  ].filter(Boolean).join(' ');

  const themeStyle = { fontSize: `${fontSize}px`, ...activeTheme.vars };

  return (
      <div className={readerClass} style={themeStyle}>
        {/* Reading progress bar */}
        <div className="pr-progress-bar">
          <div className="pr-progress-fill" style={{ width: `${readProgress}%` }} />
        </div>

        {/* Top toolbar */}
        <div className="pr-toolbar">
          <div className="pr-toolbar-inner">
            <Link to="/research" className="pr-back-btn">
              <FiArrowLeft /> Catalogue
            </Link>
            <div className="pr-toolbar-center">
              <span className="pr-reading-time">
                <FiFileText /> ~{Math.max(5, Math.ceil(sections.reduce((a, s) =>
                  a + (typeof s.content === 'string' ? s.content.length : 200), 0) / 1500))} min read
              </span>
            </div>
            <div className="pr-toolbar-actions">
              <button type="button" onClick={() => setFontSize(Math.max(12, fontSize - 2))} title="Decrease font"><FiZoomOut /></button>
              <span className="pr-font-size">{fontSize}px</span>
              <button type="button" onClick={() => setFontSize(Math.min(24, fontSize + 2))} title="Increase font"><FiZoomIn /></button>
              <div className="pr-theme-trigger-wrap" ref={themePickerRef}>
                <button type="button" onClick={() => setShowThemePicker(!showThemePicker)} title="Reader theme" className={showThemePicker ? 'active' : ''}>
                  <FiDroplet />
                </button>
                {showThemePicker && (
                  <div className="pr-theme-picker" onClick={(e) => e.stopPropagation()}>
                    <span className="pr-theme-picker-title">Reader Theme</span>
                    <div className="pr-theme-swatches">
                      {READER_THEMES.map((t) => (
                        <button
                          type="button"
                          key={t.id}
                          className={`pr-theme-swatch ${activeTheme.id === t.id ? 'active' : ''}`}
                          onClick={(e) => { e.stopPropagation(); setActiveTheme(t); }}
                          title={t.name}
                        >
                          <span className="pr-swatch-color" style={{ background: t.preview[0], borderColor: t.preview[1] }}>
                            <span className="pr-swatch-accent" style={{ background: t.preview[1] }} />
                          </span>
                          <span className="pr-swatch-label">{t.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <button type="button" onClick={() => setFocusMode(!focusMode)} title="Focus mode">
                {focusMode ? <FiMinimize2 /> : <FiMaximize2 />}
              </button>
              <button type="button" onClick={() => setTocOpen(!tocOpen)} title="Table of contents">
                <FiList />
              </button>
              <UserBadge />
            </div>
          </div>
        </div>

        <div className="pr-layout">
          {/* Table of Contents sidebar */}
          {tocOpen && !focusMode && (
            <aside className="pr-toc">
              <h3 className="pr-toc-title">Table of Contents</h3>
              <nav className="pr-toc-nav">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    className={`pr-toc-item ${activeSection === s.id ? 'active' : ''}`}
                    onClick={() => scrollToSection(s.id)}
                  >
                    <span className="pr-toc-indicator" />
                    {s.title}
                  </button>
                ))}
              </nav>

              {/* Supplementary Material */}
              <div className="pr-toc-upload">
                <h4>Supplementary Material</h4>
                {/* Published PDF from admin */}
                {paper.pdfUrl && paper.pdfUrl !== '#' && (
                  <>
                    <button
                      className="pr-view-pdf-btn"
                      onClick={() => { setPdfFile(paper.pdfUrl); setShowPdfViewer(true); }}
                    >
                      <FiFileText /> View Published PDF
                    </button>
                    <a
                      href={paper.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="pr-download-pdf-btn"
                      download
                    >
                      <FiDownload /> Download PDF
                    </a>
                  </>
                )}
                {/* User upload (local / personal) */}
                <button className="pr-upload-btn" onClick={() => fileInputRef.current?.click()}>
                  <FiUpload /> Upload Your Own PDF
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="application/pdf"
                  onChange={handlePdfUpload}
                  style={{ display: 'none' }}
                />
                {pdfFile && pdfFile !== paper.pdfUrl && (
                  <button className="pr-view-pdf-btn" onClick={() => setShowPdfViewer(true)}>
                    <FiFileText /> View Your PDF
                  </button>
                )}
              </div>

              {/* Quick actions */}
              <div className="pr-toc-actions">
                <button className="pr-toc-action"><FiDownload /> Download</button>
                <button className={`pr-toc-action${isSaved ? ' pr-toc-action--saved' : ''}`} onClick={handleToggleSave}>
                  <FiBookmark /> {isSaved ? 'Saved' : 'Save'}
                </button>
                <button className="pr-toc-action"><FiShare2 /> Share</button>
                <button className="pr-toc-action" onClick={() => setShowCiteModal(true)}><FiExternalLink /> Cite</button>
              </div>
            </aside>
          )}

          {/* Main content area */}
          <main className="pr-content" ref={contentRef}>
            {/* Paper header */}
            <header className="pr-paper-header">
              <div className="pr-category-badge">{paper.category}</div>
              <h1 className="pr-paper-title">{paper.title}</h1>
              <div className="pr-paper-authors">
                {paper.authors.map((a, i) => (
                  <span key={a} className="pr-author">
                    {a}{i < paper.authors.length - 1 && <span className="pr-author-sep">,</span>}
                  </span>
                ))}
              </div>
              <div className="pr-paper-meta">
                {paper.venue && <span className="pr-meta-item">{paper.venue}</span>}
                {paper.publishedDate && <span className="pr-meta-item">Published {paper.publishedDate}</span>}
                <span className="pr-meta-item">Cited by {paper.citedBy}</span>
              </div>
              <div className="pr-paper-tags">
                {paper.tags.map((t) => (
                  <span key={t} className="pr-tag">{t}</span>
                ))}
              </div>
            </header>

            {/* Render sections */}
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                ref={(el) => { sectionRefs.current[section.id] = el; }}
                className="pr-section"
              >
                <h2 className="pr-section-title">{section.title}</h2>

                {section.type === 'text' && <TextSection content={section.content} />}
                {section.type === 'code' && <CodeSection content={section.content} language={section.language} />}
                {section.type === 'table' && <TableSection headers={section.headers} rows={section.rows} />}
                {section.type === 'figure' && <FigureSection caption={section.caption} imageUrl={section.imageUrl} chartConfig={section.chartConfig} themeColors={activeTheme.chartColors} />}
                {section.type === 'chart' && <InteractiveChart chartConfig={section.chartConfig} caption={section.caption} themeColors={activeTheme.chartColors} />}
                {section.type === 'references' && <ReferencesSection items={section.content} />}
              </section>
            ))}
          </main>
        </div>

        {/* Scroll to top */}
        {showScrollTop && (
          <button className="pr-scroll-top" onClick={scrollToTop}>
            <FiChevronUp />
          </button>
        )}

        {/* PDF Viewer Modal */}
        {showPdfViewer && pdfFile && (
          <div className="pr-pdf-modal">
            <div className="pr-pdf-modal-header">
              <span>PDF Viewer</span>
              <button onClick={() => setShowPdfViewer(false)}><FiX /></button>
            </div>
            <iframe src={pdfFile} className="pr-pdf-iframe" title="PDF Viewer" />
          </div>
        )}

        {/* Cite Modal — Google Scholar style */}
        {showCiteModal && (
          <div className="citation-modal-overlay" onClick={() => setShowCiteModal(false)}>
            <div className="citation-modal citation-modal--reader" onClick={(e) => e.stopPropagation()}>
              <div className="citation-modal-header">
                <button className="citation-modal-close" onClick={() => setShowCiteModal(false)}>
                  <FiX />
                </button>
                <h2>Cite</h2>
              </div>
              <div className="citation-modal-body">
                {CITATION_FORMATS.map(({ key, label }) => (
                  <div className="citation-format-block" key={key}>
                    <div className="citation-format-header">
                      <span className="citation-format-label">{label}</span>
                      <button
                        className="citation-action-btn"
                        title="Copy"
                        onClick={() => handleCiteCopy(key)}
                      >
                        {citeCopied === key ? <FiCheck style={{ color: '#059669' }} /> : <FiCopy />}
                      </button>
                    </div>
                    <pre className={`citation-text ${key === 'bibtex' ? 'citation-bibtex' : ''}`}>
                      {paperCitations[key]}
                    </pre>
                  </div>
                ))}
              </div>
              <div className="citation-modal-footer">
                <button
                  className="citation-export-btn"
                  onClick={() => {
                    const blob = new Blob([paperCitations.bibtex || ''], { type: 'text/plain' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `${(paper?.title || 'paper').replace(/[^a-zA-Z0-9]/g, '_')}.bib`;
                    a.click();
                  }}
                >
                  BibTeX
                </button>
                <button
                  className="citation-export-btn"
                  onClick={() => {
                    const endnote = `%0 Journal Article\n%T ${paper?.title}\n%A ${(paper?.authors || []).join('\n%A ')}\n%J ${paper?.venue || 'Vector Labs Reports'}\n%D ${paper?.publishedDate || ''}\n`;
                    const blob = new Blob([endnote], { type: 'text/plain' });
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `${(paper?.title || 'paper').replace(/[^a-zA-Z0-9]/g, '_')}.enw`;
                    a.click();
                  }}
                >
                  EndNote
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
